import { useState, useEffect, useMemo, useRef } from 'react';
import SplitShipmentItemsTable from './SplitShipmentItemsTable';
import { ChevronsUpDown, Loader2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, EyeOff, Check } from 'lucide-react';
import { marketplaceOrdersApi } from '@/lib/api';
import { graphqlApi } from '@/lib/graphql';
import { useAuthStore } from '@/stores/auth-store';
import { useFulfillment } from '@/hooks/use-fulfillment';
import { useLocation } from 'react-router-dom';
import { useFilterStore } from '@/stores/filter-store';
import { useOperationFilterStore } from '@/stores/operation-filter-store';
import { useShipmentsStore } from '@/stores/shipments-store';
import { useStoreStore } from '@/stores/store-store';
import { useLanguage } from '@/contexts/LanguageContext';
import SplitOrdersTable from './SplitOrdersTable';
import ShipmentsTable from './ShipmentsTable';
import ShipmentItemsTable from './ShipmentItemsTable';

interface MarketplaceOrder {
  id: number;
  code: string;
  first_identifier: string;
  second_identifier: string;
  sku: string;
  weight: string;
  price: string;
  total: string;
  product_image: string;
  created_at: string;
  marketplace_order_product: {
    name: string;
    status: string;
    status_reason: string;
  };
  order: {
    reference: string;
    order_date: string;
    payment_status: string;
    email: string;
  };
}

interface GraphQLOrder {
  id: string;
  reference: string;
  order_date: string;
  status: string;
  payment_status: string;
  customer: {
    email: string;
    phone: string;
  };
  market_place_type: {
    name: string;
  };
  city: {
    name: string;
  };
  order_products_count: number;
  order_products: Array<{
    id: string;
    code: string;
    first_identifier: string;
    second_identifier: string;
    marketplace_order_product: {
      name: string;
      status?: string;
    };
    marketplace: {
      name: string;
    };
  }>;
}

interface SelectedShipmentContext {
  id: number;
  shipmentReference?: string;
  masterWayBill?: string;
}

const getStatusColor = (color: string) => {
  switch (color) {
    case 'blue':
      return 'bg-blue-50 text-blue-600 border border-blue-200';
    case 'yellow':
      return 'bg-yellow-50 text-yellow-600 border border-yellow-200';
    case 'green':
      return 'bg-green-50 text-green-600 border border-green-200';
    default:
      return 'bg-gray-50 text-gray-600 border border-gray-200';
  }
};

const getStatusColorByStatus = (status: string) => {
  // Map status to color based on Figma design
  // Handle both capitalized and lowercase/hyphenated status values from API
  const statusColorMap: { [key: string]: string } = {
    // Capitalized versions (fallback)
    'Outstanding': 'blue',
    'Payment Verification': 'yellow',
    'Ready for Procurement': 'green',
    'Fulfilled': 'blue',
    'Awaiting Resolution': 'yellow',
    'Awaiting': 'green',
    'In Facility': 'blue',
    'Destination Warehouse': 'yellow',
    'Distribution Center': 'green',
    'Delivered': 'blue',
    'TappyKash': 'yellow',
    'Voucher': 'green',
    'eWallet': 'blue',
    'CSV': 'yellow',
    
    // Lowercase/hyphenated versions (from GraphQL API)
    'outstanding': 'blue',
    'payment-verified': 'yellow',
    'pending-item-completion': 'green',
    'item-completion-payment-unverified': 'yellow',
    'item-completion-payment-verified': 'blue',
    'prepare-for-procurement': 'green',
    'procured-items': 'blue',
    'unsettled-items': 'yellow',
    'under-review': 'yellow',
    'partial-fulfillment': 'green',
    'refunded': 'green',
    'not-eligible-refund': 'yellow',
    'on-its-way': 'green',
    'delayed': 'yellow',
    'unreceived': 'yellow',
    'procured-but-not-delivered-to-facility': 'yellow',
    'delivered-to-facility': 'blue',
    'shipped-to-destination-warehouse': 'green',
    'received-at-main-warehouse': 'blue',
    'shipped-to-distribution-center': 'yellow',
    'received-at-distribution-center': 'green',
    'delivered-to-the-customer': 'blue',
    'shipped': 'blue',
    'canceled': 'yellow'
  };
  
  return getStatusColor(statusColorMap[status] || 'blue');
};

function CustomCheckbox() {
  return (
    <div className="relative">
      <input 
        type="checkbox" 
        className="peer w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600" 
      />
      <svg 
        className="absolute inset-0 w-4 h-4 pointer-events-none opacity-0 peer-checked:opacity-100 text-white" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

export default function OrdersTable({ 
  statusFilter, 
  subStatusFilter, 
  storeId,
  fulfillmentData,
  useGraphQL = false,
  showSplitTable = false,
  splitTableType,
  // onTakeInClick,
  // onBackToNormalTable,
  onShipmentSubmitClick,
  searchQuery,
  filters,
  originalStatus,
  onRefundClick,
  onViewAllClick,
}: { 
  statusFilter?: string; 
  subStatusFilter?: string; 
  storeId?: number;
  fulfillmentData?: {
    orders: any[];
    totalOrders: number;
    error: string | null;
    isLoading: boolean;
    refreshOrders: () => void;
    updateSorting: (field: string, order: 'asc' | 'desc') => void;
    sortField: string;
    sortOrder: 'asc' | 'desc';
  };
  useGraphQL?: boolean;
  showSplitTable?: boolean;
  splitTableType?: 'take-in' | 'ship' | 'to-center' | null;
  onTakeInClick?: () => void;
  onBackToNormalTable?: () => void;
  onShipmentSubmitClick?: () => void;
  searchQuery?: string;
  filters?: any;
  originalStatus?: string;
  onRefundClick?: (order: MarketplaceOrder | GraphQLOrder | any) => void;
  onViewAllClick?: (order: MarketplaceOrder | GraphQLOrder | any) => void;
}) {
  const { pathname } = useLocation();
  const isFulfillmentPage = pathname === '/fulfillment';
  const isOrdersPage = pathname === '/orders';
  const isOperationPage = pathname === '/operation';
  const useActionOrderSpacingDesign = isFulfillmentPage || isOrdersPage || isOperationPage;
  const { t } = useLanguage();
  
  // Use fulfillment data if provided, otherwise use the hook
  // Only call useFulfillment hook when NO fulfillmentData prop is passed
  const fulfillmentHookData = useFulfillment({
    storeId,
    autoFetch: !fulfillmentData && !!(isFulfillmentPage || isOrdersPage)
  });

  // Use provided fulfillment data or hook data
  const finalFulfillmentData = fulfillmentData || fulfillmentHookData;

  // Get pagination state from fulfillment store
  const { fulfillmentFilters, setFulfillmentPagination } = useFilterStore();
  const page = fulfillmentFilters.page;
  const itemsPerPage = fulfillmentFilters.limit;

  // Get operation filter state
  const {
    searchQuery: operationSearchQuery,
    filters: operationFilters,
    marketplaceFilter: operationMarketplaceFilter
  } = useOperationFilterStore();

  // Get shipments store for local warehouse REST endpoints
  const {
    fetchIncomingShipments,
    fetchArrivedShipments,
    fetchOutboundDistribution,
    fetchUnconfirmedOrders,
    fetchDistributionIncomingShipments,
    fetchDistributionArrivedShipments,
    fetchDistributionReturnedShipments,
    fetchDistributionMissingShipments,
    shipments,
    total: shipmentsTotal,
    isLoading: shipmentsLoading,
    clearSearch,
  } = useShipmentsStore();
  const { currentStore } = useStoreStore();
  // Derive store_name slug for warehouse/incoming-shipments endpoint (mirrors legacy incomingShipments)
  const storeName = currentStore?.name?.toLowerCase()?.replace(/\s+/g, '') ?? '';

  const [orders, setOrders] = useState<(MarketplaceOrder | GraphQLOrder)[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use shipments store loading state when fetching local shipments
  const isLocalShipmentsLoading = originalStatus === 'destination_warehouse' && subStatusFilter === 'inbound' && shipmentsLoading;
  // City/Area controls moved to top toolbar
  
  // Split table state for destination warehouse inbound
  const [selectedShipment, setSelectedShipment] = useState<SelectedShipmentContext | null>(null);
  const [showShipmentSplitTable, setShowShipmentSplitTable] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [headerMenu, setHeaderMenu] = useState<{ column: string; field: string } | null>(null);
  const [headerMenuFilter, setHeaderMenuFilter] = useState('');
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>({});
  const headerMenuRef = useRef<HTMLDivElement | null>(null);

  // Handle Receive button click for destination warehouse inbound
  const handleReceiveClick = (shipment: SelectedShipmentContext) => {
    setSelectedShipment(shipment);
    setShowShipmentSplitTable(true);
  };

  const handleBackToShipments = () => {
    setShowShipmentSplitTable(false);
    setSelectedShipment(null);
    
    // Refresh the orders data when navigating back from split table
    console.log('🔄 Refreshing orders data after split table...');
    fetchOrders();
    
    // Dispatch refresh event to update other components
    const refreshEvent = new CustomEvent('refresh-operation-data');
    window.dispatchEvent(refreshEvent);
  };
  // const [searchTerm, setSearchTerm] = useState('');
  const { token } = useAuthStore();
  const totalPages = Math.ceil(total / itemsPerPage);
  const maxVisiblePages = 5;
  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 0) return [];

    const halfWindow = Math.floor(maxVisiblePages / 2);
    let startPage = Math.max(1, page - halfWindow);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }, [page, totalPages]);
  const showLeadingEllipsis = visiblePageNumbers.length > 0 && visiblePageNumbers[0] > 1;
  const showTrailingEllipsis = visiblePageNumbers.length > 0 && visiblePageNumbers[visiblePageNumbers.length - 1] < totalPages;

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setFulfillmentPagination(newPage, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setFulfillmentPagination(1, newItemsPerPage); // Reset to page 1 when changing items per page
  };

  const toggleHeaderMenu = (column: string, field: string) => {
    setHeaderMenuFilter('');
    setHeaderMenu((prev) => (prev?.column === column ? null : { column, field }));
  };

  const handleHeaderMenuSort = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
    if (finalFulfillmentData?.updateSorting) {
      finalFulfillmentData.updateSorting(field, order);
    }
    setHeaderMenu(null);
  };

  const hideColumn = (column: string) => {
    setHiddenColumns((prev) => ({ ...prev, [column]: true }));
    setHeaderMenu(null);
  };

  const renderHeaderMenu = (column: string, field: string) => {
    if (headerMenu?.column !== column) return null;

    const query = headerMenuFilter.trim().toLowerCase();
    const showAsc = 'asc'.includes(query);
    const showDesc = 'desc'.includes(query);
    const showHide = 'hide'.includes(query);

    return (
      <div
        ref={headerMenuRef}
        className="absolute left-1 top-full z-30 mt-2 w-64 rounded-xl border border-[#D9DFEC] bg-white p-3 shadow-lg"
      >
        <input
          type="text"
          placeholder="Filter..."
          value={headerMenuFilter}
          onChange={(e) => setHeaderMenuFilter(e.target.value)}
          className="mb-3 w-full rounded-xl border border-[#D9DFEC] px-4 py-2.5 text-[14px] text-[#4B5675] outline-none placeholder:text-[#7A86A4] focus:border-[#BEC8E0]"
        />
        <div className="-mx-3 border-t border-[#E5EAF4]" />
        <div className="py-1">
          {showAsc && (
            <button
              type="button"
              onClick={() => handleHeaderMenuSort(field, 'asc')}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[15px] text-[#43557B] hover:bg-[#F7F9FD]"
            >
              <span className="inline-flex items-center gap-2">
                <ArrowUp size={18} className="text-[#6E7FA3]" />
                Asc
              </span>
              {sortField === field && sortOrder === 'asc' ? <Check size={18} className="text-[#3D4F75]" /> : null}
            </button>
          )}
          {showDesc && (
            <button
              type="button"
              onClick={() => handleHeaderMenuSort(field, 'desc')}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[15px] text-[#43557B] hover:bg-[#F7F9FD]"
            >
              <span className="inline-flex items-center gap-2">
                <ArrowDown size={18} className="text-[#6E7FA3]" />
                Desc
              </span>
              {sortField === field && sortOrder === 'desc' ? <Check size={18} className="text-[#3D4F75]" /> : null}
            </button>
          )}
        </div>
        <div className="-mx-3 border-t border-[#E5EAF4]" />
        <div className="pt-1">
          {showHide && (
            <button
              type="button"
              onClick={() => hideColumn(column)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[15px] text-[#43557B] hover:bg-[#F7F9FD]"
            >
              <span className="inline-flex items-center gap-2">
                <EyeOff size={18} className="text-[#6E7FA3]" />
                Hide
              </span>
            </button>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!headerMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target as Node)) {
        setHeaderMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [headerMenu]);

  // Use fulfillment data if available
  useEffect(() => {
    if ((isFulfillmentPage || isOrdersPage) && finalFulfillmentData) {
      setOrders(finalFulfillmentData.orders || []);
      setTotal(finalFulfillmentData.totalOrders || 0);
      setIsLoading(finalFulfillmentData.isLoading || false);
      setError(finalFulfillmentData.error || null);
    }
  }, [isFulfillmentPage, isOrdersPage, finalFulfillmentData?.orders, finalFulfillmentData?.totalOrders, finalFulfillmentData?.isLoading, finalFulfillmentData?.error]);

  // Synchronize component's sorting state with hook's sorting state
  useEffect(() => {
    if ((isFulfillmentPage || isOrdersPage) && finalFulfillmentData?.sortField && finalFulfillmentData?.sortOrder) {
      console.log('🔍 OrdersTable - Syncing sorting state from hook:', finalFulfillmentData.sortField, finalFulfillmentData.sortOrder);
      console.log('🔍 OrdersTable - Current component sortField:', sortField, 'sortOrder:', sortOrder);
      setSortField(finalFulfillmentData.sortField);
      setSortOrder(finalFulfillmentData.sortOrder);
    }
  }, [isFulfillmentPage, isOrdersPage, finalFulfillmentData?.sortField, finalFulfillmentData?.sortOrder]);

  // Debug logging for sorting state changes
  useEffect(() => {
    console.log('🔍 OrdersTable - Component sorting state changed:', { sortField, sortOrder });
  }, [sortField, sortOrder]);

  // Determine if Destination Warehouse → In-Warehouse (City/Area are selectors, not filters)
  const isDestInWarehouse = originalStatus === 'destination_warehouse' && subStatusFilter === 'in_warehouse';
  const isDestInbound = originalStatus === 'destination_warehouse' && subStatusFilter === 'inbound';
  const isDestOutboundDistribution = originalStatus === 'destination_warehouse' && subStatusFilter === 'outbound';
  const isDestUnconfirmed = originalStatus === 'destination_warehouse' && (subStatusFilter === 'unconfirmed' || subStatusFilter === 'missing');
  const isDistributionInbound = originalStatus === 'distribution_center' && subStatusFilter === 'inbound';
  const isDistributionInCenter = originalStatus === 'distribution_center' && subStatusFilter === 'in_center';
  const isDistributionReturn = originalStatus === 'distribution_center' && subStatusFilter === 'return';
  const isDistributionMissing = originalStatus === 'distribution_center' && subStatusFilter === 'missing';
  const isDestinationWarehouseRestMode =
    originalStatus === 'destination_warehouse' &&
    ['inbound', 'in_warehouse', 'outbound', 'unconfirmed', 'missing'].includes(subStatusFilter || '');
  const isDistributionCenterRestMode =
    originalStatus === 'distribution_center' &&
    ['inbound', 'in_center', 'return', 'missing'].includes(subStatusFilter || '');
  const isDestWarehouseLocal = originalStatus === 'destination_warehouse' && (subStatusFilter === 'inbound' || subStatusFilter === 'in_warehouse');

  // Filter out City/Area from fetch dependencies for this subtab to avoid refetch on change
  const fetchFilters = useMemo(() => {
    if (!operationFilters) return {} as any;
    if (!isDestInWarehouse) return operationFilters;
    const { city, area, ...rest } = operationFilters as any;
    return rest;
  }, [operationFilters, isDestInWarehouse]);

  // City/Area moved to top toolbar

  // Map status filter to API type parameter
  const getApiType = (statusFilter?: string): string => {
    const statusMap: { [key: string]: string } = {
      'outstanding': 'outstanding',
      'payment_verification': 'payment-verified',
      'ready_for_procurement': 'ready-for-procurement',
      'fulfilled': 'fulfilled',
      'awaiting_resolution': 'awaiting-resolution',
      'awaiting': 'awaiting',
      'in_facility': 'in-facility',
      'destination_warehouse': 'destination-warehouse',
      'distribution_center': 'distribution-center',
      'delivered': 'delivered',
      'tappykash': 'tappykash',
      'voucher': 'voucher',
      'ewallet': 'ewallet',
      'csv': 'csv'
    };
    return statusMap[statusFilter || ''] || 'outstanding';
  };

  // Fetch orders from API
  const fetchOrders = async () => {
    if (!token || !storeId) return;

    // Only set local loading state if not using shipments store
    if (!(isDestinationWarehouseRestMode || isDistributionCenterRestMode)) {
      setIsLoading(true);
    }
    setError(null);

    try {
             if (useGraphQL) {
         // Use GraphQL API - statusFilter is already mapped from the operation page
         // Use operation filter store values if available, otherwise fall back to props
         const finalSearchQuery = operationSearchQuery || searchQuery || '';
         const finalFilters = operationFilters || filters || {};
         
         // Use marketplace filter directly (uk_mkt, us_mkt format)
         let marketPlaceType = finalFilters.marketPlaceType || '';
         if (operationMarketplaceFilter && operationMarketplaceFilter.length > 0) {
           // Use the marketplace filter values directly (uk_mkt, us_mkt format)
           marketPlaceType = operationMarketplaceFilter.join(',');
         }

         // Debug logging for operation page
         if (pathname === '/operation') {
           console.log('Operation Page GraphQL Request:', {
             status: statusFilter,
             search: finalSearchQuery,
             marketPlaceType: marketPlaceType,
             city: finalFilters.city,
             customerEmail: finalFilters.customerEmail,
             orderDateFrom: finalFilters.orderDateFrom,
             orderDateTo: finalFilters.orderDateTo,
             page,
             first: itemsPerPage,
             operationSearchQuery,
             operationFilters,
             operationMarketplaceFilter
           });
         }

         // Format dates to match GraphQL DateTime format (Y-m-d H:i:s)
         const formatDateForGraphQL = (dateString: string | undefined) => {
           if (!dateString) return undefined;
           return `${dateString} 00:00:00`;
         };

         const response = await graphqlApi.getOrderListing(token, storeId, {
           status: statusFilter || '',
           search: finalSearchQuery,
           marketPlaceType: marketPlaceType,
           city: isDestInWarehouse ? '' : (finalFilters.city || ''),
           customerEmail: finalFilters.customerEmail || '',
           orderDateFrom: formatDateForGraphQL(finalFilters.orderDateFrom),
           orderDateTo: formatDateForGraphQL(finalFilters.orderDateTo),
           page,
           first: itemsPerPage,
           sort: sortField,
           order: sortOrder,
         });

        if (response.errors) {
          throw new Error(response.errors[0]?.message || 'GraphQL query failed');
        }

        if (response.data?.orderListing) {
          setOrders(response.data.orderListing.data || []);
          setTotal(response.data.orderListing.paginatorInfo.count || 0);
        } else {
          setOrders([]);
          setTotal(0);
        }
      } else {
        // Use REST API
        // Check if this is for destination warehouse inbound (should use local-shipments API)
        if (isDestInbound) {
          // Use warehouse/incoming-shipments — mirrors legacy incomingShipments exactly
          // (one row per shipment grouped by shipments.id, same 5-condition filter)
          const result = await fetchIncomingShipments({
            token,
            storeName,
            page,
            limit: itemsPerPage,
            search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
          });

          // Transform to shape expected by OrdersTable row renderer
          // Fields are already normalized by fetchIncomingShipments:
          //   sh_shipment_reference → shipment_reference
          //   masterway_bill        → master_way_bill
          //   shcreated_at          → created_at
          //   total_amount          → total
          const transformedData = (result.shipments || []).map((shipment: any) => ({
            ...shipment,
            order_date: shipment.created_at,
            order: {
              order_date: shipment.created_at,
              email: 'N/A',
            },
            first_identifier: shipment.first_identifier || shipment.code || '',
            second_identifier: shipment.second_identifier || '',
            isShipmentData: true
          }));
          
          setOrders(transformedData);
          setTotal(result.total || 0);

          // Check for shipments store errors
        } else if (isDestInWarehouse) {
          // Use warehouse/arrived-shipments — mirrors legacy local-warehouse arrived items
          const result = await fetchArrivedShipments({
            token,
            storeName,
            page,
            limit: itemsPerPage,
            search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
          });

          const transformedData = (result.shipments || []).map((item: any) => ({
            ...item,
            code: item.item_order_id || item.code,
            master_way_bill: item.masterway_bill || item.master_way_bill,
            total: item.total || item.cost || null,
            isShipmentData: true,
            isArrivedData: true,
          }));

          setOrders(transformedData);
          setTotal(result.total || 0);
        } else if (isDestOutboundDistribution) {
          // Use warehouse/outbound-distribution — mirrors legacy local-warehouse outbound distribution
          const result = await fetchOutboundDistribution({
            token,
            storeName,
            page,
            limit: itemsPerPage,
            search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
          });

          const transformedData = (result.shipments || []).map((item: any) => ({
            ...item,
            code: item.item_order_id || item.code,
            master_way_bill: item.masterway_bill || item.master_way_bill,
            total: item.total || item.cost || null,
            isShipmentData: true,
            isOutboundDistributionData: true,
          }));

          setOrders(transformedData);
          setTotal(result.total || 0);
        } else if (isDestUnconfirmed) {
          // Use warehouse/unconfirmed-orders — mirrors legacy local-warehouse unconfirmed orders
          const result = await fetchUnconfirmedOrders({
            token,
            storeName,
            page,
            limit: itemsPerPage,
            search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
          });

          const transformedData = (result.shipments || []).map((item: any) => ({
            ...item,
            code: item.item_order_id || item.code,
            master_way_bill: item.masterway_bill || item.master_way_bill,
            total: item.total || item.cost || null,
            isShipmentData: true,
            isUnconfirmedData: true,
          }));

          setOrders(transformedData);
          setTotal(result.total || 0);
        } else if (isDistributionCenterRestMode) {
          const distributionCenterId = Number(operationFilters?.distributionCenterId || filters?.distributionCenterId || 0);

          if (!distributionCenterId) {
            setOrders([]);
            setTotal(0);
            return;
          }

          if (isDistributionInbound) {
            const result = await fetchDistributionIncomingShipments({
              token,
              storeName,
              distributionCenterId,
              page,
              limit: itemsPerPage,
              search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
            });
            setOrders((result.shipments || []).map((item: any) => ({ ...item, isDistributionBatchData: true })));
            setTotal(result.total || 0);
          } else if (isDistributionInCenter) {
            const result = await fetchDistributionArrivedShipments({
              token,
              storeName,
              distributionCenterId,
              page,
              limit: itemsPerPage,
              search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
            });
            setOrders((result.shipments || []).map((item: any) => ({ ...item, isDistributionBatchData: true })));
            setTotal(result.total || 0);
          } else if (isDistributionReturn) {
            const result = await fetchDistributionReturnedShipments({
              token,
              storeName,
              distributionCenterId,
              page,
              limit: itemsPerPage,
              search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
            });
            setOrders((result.shipments || []).map((item: any) => ({ ...item, isDistributionBatchData: true })));
            setTotal(result.total || 0);
          } else if (isDistributionMissing) {
            const result = await fetchDistributionMissingShipments({
              token,
              storeName,
              distributionCenterId,
              page,
              limit: itemsPerPage,
              search: operationSearchQuery && operationSearchQuery.trim() !== '' ? operationSearchQuery : undefined,
            });
            setOrders((result.shipments || []).map((item: any) => ({ ...item, isDistributionMissingData: true })));
            setTotal(result.total || 0);
          }
        } else {
          // Use marketplace orders API for other cases
          const apiType = getApiType(statusFilter);
          const response = await marketplaceOrdersApi.getOrders(token, storeId, apiType, page, itemsPerPage);

          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch orders');
          }

          setOrders(response.data?.data || []);
          setTotal(response.data?.count || 0);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch orders';
      setError(errorMessage);
      console.error('Error fetching orders:', err);
    } finally {
      // Only reset local loading state if not using shipments store
      if (!(isDestinationWarehouseRestMode || isDistributionCenterRestMode)) {
        setIsLoading(false);
      }
    }
  };

  // Check if we should show shipment items table (only for In Facility Outbound now)
  // Destination Warehouse Inbound is handled by OrdersTable with local-shipments API
  const shouldShowShipmentItemsTable = 
    (originalStatus === 'in_facility' && subStatusFilter === 'outbound');

  // Fetch orders when dependencies change
  useEffect(() => {
    if (!isFulfillmentPage && !isOrdersPage && !shouldShowShipmentItemsTable) {
      fetchOrders();
    }
  }, [
    statusFilter, 
    storeId, 
    page, 
    itemsPerPage, 
    token, 
    isFulfillmentPage, 
    isOrdersPage, 
    searchQuery, 
    filters,
    // Operation filter store dependencies
    operationSearchQuery,
    fetchFilters,
    operationMarketplaceFilter,
    shouldShowShipmentItemsTable,
    // Sorting dependencies
    sortField,
    sortOrder,
  ]);

  // Reset to first page when status filter changes
  useEffect(() => {
    if (!isFulfillmentPage && !isOrdersPage) {
      handlePageChange(1);
    }
  }, [statusFilter, isFulfillmentPage, isOrdersPage]);

  // Reset to first page when sorting changes
  useEffect(() => {
    if (!isFulfillmentPage && !isOrdersPage) {
      handlePageChange(1);
    } else if (isFulfillmentPage || isOrdersPage) {
      // For fulfillment pages, reset pagination to page 1 when sorting changes
      setFulfillmentPagination(1, itemsPerPage);
    }
  }, [sortField, sortOrder, isFulfillmentPage, isOrdersPage, setFulfillmentPagination, itemsPerPage]);

  // Listen for refresh events from operation page
  useEffect(() => {
    const handleRefreshOperationData = () => {
      if (!isFulfillmentPage && !isOrdersPage && !shouldShowShipmentItemsTable) {
        console.log('🔄 Refreshing operation data...');
        fetchOrders();
      }
    };

    window.addEventListener('refresh-operation-data', handleRefreshOperationData);
    
    return () => {
      window.removeEventListener('refresh-operation-data', handleRefreshOperationData);
    };
  }, [isFulfillmentPage, isOrdersPage, shouldShowShipmentItemsTable, fetchOrders]);

  // Handle search clearing for local shipments
  useEffect(() => {
    if (isDestInbound) {
      // If search is cleared, clear the shipments store and refetch
      if (!operationSearchQuery || operationSearchQuery.trim() === '') {
        clearSearch();
        if (token && storeName) {
          const result = fetchIncomingShipments({
            token,
            storeName,
            page,
            limit: itemsPerPage,
          });
          
          result.then((data) => {
            if (data.shipments && data.shipments.length > 0) {
              const transformedData = data.shipments.map((shipment: any) => ({
                ...shipment,
                order_date: shipment.created_at,
                order: {
                  order_date: shipment.created_at,
                  email: 'N/A',
                },
                first_identifier: shipment.first_identifier || shipment.code || '',
                second_identifier: shipment.second_identifier || '',
                isShipmentData: true
              }));
              
              setOrders(transformedData);
              setTotal(data.total || 0);
            }
          });
        }
      }
    }
  }, [operationSearchQuery, isDestInbound, token, storeName, page, itemsPerPage, clearSearch, fetchIncomingShipments]);

  // Reset shipments store when switching tabs or unmounting
  useEffect(() => {
    return () => {
      if (isDestinationWarehouseRestMode || isDistributionCenterRestMode) {
        console.log('🔄 Component unmounting or tab switching, clearing shipments store...');
        clearSearch();
      }
    };
  }, [isDestinationWarehouseRestMode, isDistributionCenterRestMode, clearSearch]);

  // Show loading state
  const shouldShowLoading = isLoading || isLocalShipmentsLoading;
  
  if (shouldShowLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm mt-[-16px] border border-gray-200 mx-16 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600">{t('common.loading')}...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm mt-[-16px] border border-gray-200 mx-16 p-8">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">{t('table.errorLoadingOrders')}</div>
          <div className="text-gray-600 text-sm mb-4">{error}</div>
          <button 
            onClick={fetchOrders}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Show empty state
  if (orders.length === 0 && originalStatus !== 'distribution_center') {
    // Debug logging for empty state
    console.log('🔍 OrdersTable - Empty state triggered:');
    console.log('🔍 OrdersTable - orders state:', orders);
    console.log('🔍 OrdersTable - total state:', total);
    console.log('🔍 OrdersTable - shipments from store:', shipments);
    console.log('🔍 OrdersTable - shipmentsTotal from store:', shipmentsTotal);
    
    return (
      <div className="bg-white rounded-2xl shadow-sm mt-[-16px] border border-gray-200 mx-16 p-8">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-2">{t('table.noOrdersFound')}</div>
          <div className="text-gray-400 text-sm">{t('table.noOrdersMatchCriteria')}</div>
        </div>
      </div>
    );
  }

  // Check if we should show the split table design
  const shouldShowSplitTable = showSplitTable && ((subStatusFilter === 'in_bound' || subStatusFilter === 'in_facility') || (originalStatus === 'destination_warehouse' && subStatusFilter === 'in_warehouse'));

  // Check if we should show shipments table (for Outbound subtab)
  const shouldShowShipmentsTable = originalStatus === 'in_facility' && subStatusFilter === 'outbound';
  
  // Debug logging for shipments table condition
  console.log('ShipmentsTable Debug:', {
    originalStatus,
    subStatusFilter,
    shouldShowShipmentsTable,
    storeId
  });

  // const totalItems = 52;
  // const startItem = (page - 1) * itemsPerPage + 1;
  // const endItem = Math.min(page * itemsPerPage, totalItems);

  // const SearchInput = ({ placeholder }: { placeholder: string }) => (
  //   <div className="relative">
  //     <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
  //     <input
  //       type="text"
  //       placeholder={placeholder}
  //       value={searchTerm}
  //       onChange={(e) => setSearchTerm(e.target.value)}
  //       className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
  //     />
  //   </div>
  // );

  // If destination warehouse inbound shipment is selected for receiving, show split table
  if (showShipmentSplitTable && selectedShipment && originalStatus === 'destination_warehouse' && subStatusFilter === 'inbound') {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16">
        {/* Header with back button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToShipments}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={16} />
              Back to Shipments
            </button>
            <h2 className="text-lg font-semibold text-gray-900">
              Receiving Shipment: {selectedShipment.shipmentReference || selectedShipment.id}
            </h2>
          </div>
        </div>

        {/* Use SplitShipmentItemsTable component for shipment items */}
        <SplitShipmentItemsTable 
          shipmentId={selectedShipment.id}
          storeId={storeId || 0}
          shipmentReference={selectedShipment.shipmentReference}
          masterWayBill={selectedShipment.masterWayBill}
          onShipmentSubmitClick={onShipmentSubmitClick}
        />
      </div>
    );
  }

  // If it's the split table, show the new design
  if (shouldShowSplitTable) {
    // Use operation filter store values if available, otherwise fall back to props
    const finalSearchQuery = operationSearchQuery || searchQuery || '';
    const finalFilters = operationFilters || filters || {};
    
    // Determine the status for the split table based on the type
    let splitTableStatus: string | undefined;
    const typeStr = (splitTableType ?? undefined) as string | undefined;
    if (typeStr === 'ship') {
      // For Ship button, use delivered-to-facility status
      splitTableStatus = 'delivered-to-facility';
    } else if (typeStr === 'take-in') {
      // For Take-in button, use procured-but-not-delivered-to-facility status
      splitTableStatus = 'procured-but-not-delivered-to-facility';
    } else if (typeStr === 'to-center') {
      // For To Center, use received-at-destination-warehouse as the left table source
      splitTableStatus = 'received-at-destination-warehouse';
    } else {
      // Fallback to statusFilter if splitTableType is not set
      splitTableStatus = statusFilter;
    }
    
         return (
       <SplitOrdersTable 
         searchQuery={finalSearchQuery}
         filters={{
           ...finalFilters,
           marketplaceFilter: operationMarketplaceFilter
         }}
         storeId={storeId}
         status={splitTableStatus}
       />
     );
   }

   // If it's the shipments table, show the shipments design
   if (shouldShowShipmentsTable) {
     return (
       <ShipmentsTable 
         storeId={storeId}
         searchQuery={searchQuery}
         filters={filters}
       />
     );
   }

   // If it's the shipment items table, show the shipment items design
   if (shouldShowShipmentItemsTable) {
     return (
       <ShipmentItemsTable onShipmentSubmitClick={onShipmentSubmitClick} />
     );
   }

  // Original table design for other sub-statuses
  const formatDate = (value?: string | null) => {
    if (!value) return 'N/A';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return 'N/A';
    return parsed.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (isDestOutboundDistribution) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16" data-component="OrdersTableContainer">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#EDF1FF] border-b border-gray-200">
                <th className="py-3 px-4 text-left border-r border-gray-200"><CustomCheckbox /></th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Actions</th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Batch ID</th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Date</th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Packages</th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">From</th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Dimensions</th>
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">Weight</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map((order: any) => (
                <tr key={`${order.id}-${order.batch_id || order.order_product_id || ''}`} className="hover:bg-gray-50">
                  <td className="py-3 px-4 border-r border-gray-200"><CustomCheckbox /></td>
                  <td className="py-3 px-4 border-r border-gray-200">
                    <button className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-400 text-sm font-medium">
                      Adjustment
                    </button>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.batch_id || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{formatDate(order.batch_id_created_at || order.created_at)}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.total_packages || order.qty || 1}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.market_place_type || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.dimensions || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm text-gray-900">{order.weight || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select 
              value={itemsPerPage} 
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            <span className="text-sm text-gray-700">per page</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-700">{`${((page - 1) * itemsPerPage) + 1}-${Math.min(page * itemsPerPage, total)} of ${total}`}</span>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>

              {showLeadingEllipsis && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-1 text-sm rounded text-gray-500 hover:bg-gray-100"
                  >
                    1
                  </button>
                  <span className="px-1 text-sm text-gray-400">...</span>
                </>
              )}

              {visiblePageNumbers.map((pageNum) => {
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded ${
                      page === pageNum ? 'bg-[#F1F1F4] text-gray-900 font-medium' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {showTrailingEllipsis && (
                <>
                  <span className="px-1 text-sm text-gray-400">...</span>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-1 text-sm rounded text-gray-500 hover:bg-gray-100"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button 
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (originalStatus === 'distribution_center') {
    const selectedDestination = operationFilters?.area || operationFilters?.city || filters?.area || filters?.city || 'N/A';
    const sourceStore = currentStore?.name || 'N/A';

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16" data-component="OrdersTableContainer">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#EDF1FF] border-b border-gray-200">
                <th className="py-3 px-4 text-left border-r border-gray-200"><CustomCheckbox /></th>
                {isDistributionMissing ? (
                  <>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Shipment Ref</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Item Order ID</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Master WayBill</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Combine ID</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Split Status</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Product Name</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">MKT Order no.</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">MKT Name</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">MKT Name URL</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Order Status</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">Action</th>
                  </>
                ) : (
                  <>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Actions</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Batch ID</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Order Date</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Packages</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">From</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Destination</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">Dimensions</th>
                    <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">Weight</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map((order: any) => (
                <tr key={`${order.id}-${order.batch_id || order.order_product_id || ''}`} className="hover:bg-gray-50">
                  <td className="py-3 px-4 border-r border-gray-200"><CustomCheckbox /></td>
                  {isDistributionMissing ? (
                    <>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.shipment_reference || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.item_order_id || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.master_way_bill || order.masterway_bill || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.combine_id || '-'}</td>
                      <td className="py-3 px-4 text-sm text-blue-500 border-r border-gray-200">{order.split_status || 'Not Split'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.mkt_product_name || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.mkt_order_number || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.mkt_name || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200 max-w-[140px] truncate">{order.mkt_url || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">Missing</td>
                      <td className="py-3 px-4">
                        <button className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-yellow-400 text-gray-900 text-sm font-semibold hover:bg-yellow-500 transition-colors">
                          Found
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 px-4 border-r border-gray-200">
                        <button className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-400 text-sm font-medium">
                          Adjustment
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.batch_id || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{formatDate(order.batch_id_created_at || order.order_confirmed_date || order.created_at || order.distribution_center_created_at)}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.total_packages || order.packages || order.qty || 1}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{sourceStore}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{selectedDestination}</td>
                      <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">{order.dimensions || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-900">{order.weight ? `${order.weight}kg` : 'N/A'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select 
              value={itemsPerPage} 
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            <span className="text-sm text-gray-700">per page</span>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-700">{`${((page - 1) * itemsPerPage) + 1}-${Math.min(page * itemsPerPage, total)} of ${total}`}</span>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>

              {showLeadingEllipsis && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-1 text-sm rounded text-gray-500 hover:bg-gray-100"
                  >
                    1
                  </button>
                  <span className="px-1 text-sm text-gray-400">...</span>
                </>
              )}

              {visiblePageNumbers.map((pageNum) => {
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded ${
                      page === pageNum ? 'bg-[#F1F1F4] text-gray-900 font-medium' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {showTrailingEllipsis && (
                <>
                  <span className="px-1 text-sm text-gray-400">...</span>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-1 text-sm rounded text-gray-500 hover:bg-gray-100"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button 
                onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16" data-component="OrdersTableContainer">
      {/* Destination Warehouse City/Area moved to FilterToolbar */}
      {/* Table */}
      <div className="overflow-x-auto">
        <table className={`w-full ${isFulfillmentPage ? 'fulfillment-compact-table' : ''}`}>
          <thead>
            <tr className="bg-[#EDF1FF] border-b border-gray-200">
              <th className="py-3 px-4 text-left border-r border-gray-200">
                <CustomCheckbox />
              </th>
              {!hiddenColumns.actions && (
                <th className={`relative py-3 text-left text-sm font-medium text-gray-700 ${useActionOrderSpacingDesign ? 'px-4 pr-8' : 'px-4 border-r border-gray-200'}`}>
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Actions' : t('common.actions')}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleHeaderMenu('actions', 'id');
                      }}
                      className="rounded p-0.5 hover:bg-[#E8ECF6]"
                    >
                      <ChevronsUpDown size={14} className="text-gray-400" />
                    </button>
                  </div>
                  {renderHeaderMenu('actions', 'id')}
                </th>
              )}
              {!hiddenColumns.orderId && (
                <th className={`relative py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 ${useActionOrderSpacingDesign ? 'pl-8' : ''}`}>
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'References' : 'Order ID'}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleHeaderMenu('orderId', 'id');
                      }}
                      className="rounded p-0.5 hover:bg-[#E8ECF6]"
                    >
                      <ChevronsUpDown
                        size={14}
                        className={`${
                          sortField === 'id'
                            ? sortOrder === 'asc'
                              ? 'text-blue-600 rotate-180'
                              : 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      />
                    </button>
                  </div>
                  {renderHeaderMenu('orderId', 'id')}
                </th>
              )}

              {!hiddenColumns.zone && (
                <th className="relative py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Order Date' : 'Zone'}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleHeaderMenu('zone', 'created_at');
                      }}
                      className="rounded p-0.5 hover:bg-[#E8ECF6]"
                    >
                      <ChevronsUpDown
                        size={14}
                        className={`${
                          sortField === 'created_at'
                            ? sortOrder === 'asc'
                              ? 'text-blue-600 rotate-180'
                              : 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      />
                    </button>
                  </div>
                  {renderHeaderMenu('zone', 'created_at')}
                </th>
              )}

              {!hiddenColumns.mkt && (
                <th className="relative py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Marketplace' : 'MKT'}
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleHeaderMenu('mkt', 'mkt_name'); }} className="rounded p-0.5 hover:bg-[#E8ECF6]">
                      <ChevronsUpDown size={14} className={`${sortField === 'mkt_name' ? sortOrder === 'asc' ? 'text-blue-600 rotate-180' : 'text-blue-600' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {renderHeaderMenu('mkt', 'mkt_name')}
                </th>
              )}
              {!hiddenColumns.orderDate && (
                <th className="relative py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Quantity' : 'Order Date'}
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleHeaderMenu('orderDate', 'qty'); }} className="rounded p-0.5 hover:bg-[#E8ECF6]">
                      <ChevronsUpDown size={14} className={`${sortField === 'qty' ? sortOrder === 'asc' ? 'text-blue-600 rotate-180' : 'text-blue-600' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {renderHeaderMenu('orderDate', 'qty')}
                </th>
              )}
              {!hiddenColumns.identifier && (
                <th className="relative py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Master Way Bill' : 'Identifier'}
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleHeaderMenu('identifier', 'master_way_bill'); }} className="rounded p-0.5 hover:bg-[#E8ECF6]">
                      <ChevronsUpDown size={14} className={`${sortField === 'master_way_bill' ? sortOrder === 'asc' ? 'text-blue-600 rotate-180' : 'text-blue-600' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {renderHeaderMenu('identifier', 'master_way_bill')}
                </th>
              )}
              {!hiddenColumns.shippingTo && (
                <th className="relative py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200">
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Weight' : 'Shipping To'}
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleHeaderMenu('shippingTo', 'weight'); }} className="rounded p-0.5 hover:bg-[#E8ECF6]">
                      <ChevronsUpDown size={14} className={`${sortField === 'weight' ? sortOrder === 'asc' ? 'text-blue-600 rotate-180' : 'text-blue-600' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {renderHeaderMenu('shippingTo', 'weight')}
                </th>
              )}
              {!hiddenColumns.status && (
                <th className="relative py-3 px-4 text-left text-sm font-medium text-gray-700">
                  <div className="flex items-center gap-1">
                    {isDestWarehouseLocal ? 'Total' : t('common.status')}
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggleHeaderMenu('status', 'total'); }} className="rounded p-0.5 hover:bg-[#E8ECF6]">
                      <ChevronsUpDown size={14} className={`${sortField === 'total' ? sortOrder === 'asc' ? 'text-blue-600 rotate-180' : 'text-blue-600' : 'text-gray-400'}`} />
                    </button>
                  </div>
                  {renderHeaderMenu('status', 'total')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders.map((order) => {
              const isGraphQLOrder = 'reference' in order;
              const isShipmentData = (order as any).isShipmentData === true;
              const isAdjustmentAction = isDestOutboundDistribution;
              const showRefundButton =
                !!onRefundClick &&
                isFulfillmentPage &&
                statusFilter === 'awaiting_resolution';
              
              // Helper function to get identifier for GraphQL orders
              const getGraphQLIdentifier = (order: any) => {
                if (order.order_products && order.order_products.length > 0) {
                  const firstProduct = order.order_products[0];
                  // Use first_identifier and second_identifier directly from the order product
                  if (firstProduct.first_identifier && firstProduct.second_identifier) {
                    return `${firstProduct.first_identifier}-${firstProduct.second_identifier}`;
                  }
                }
                return order.reference; // Fallback
              };
              
              return (
                <tr
                  key={isGraphQLOrder
                    ? `${order.id}-${order.order_products?.[0]?.id || 'row'}`
                    : order.id}
                  className="hover:bg-gray-50"
                >
                  <td className="py-3 px-4 border-r border-gray-200">
                    <CustomCheckbox />
                  </td>
                  {!hiddenColumns.actions && (
                  <td className={`py-3 px-4 ${useActionOrderSpacingDesign ? 'pr-8' : 'border-r border-gray-200'}`}>
                    <div className="flex flex-col gap-2">
                      {!showRefundButton && (
                        <button 
                          onClick={() => {
                            if (isDestInbound) {
                              const shipmentId = isShipmentData ? (order as any).shid || order.id : order.id;
                              handleReceiveClick({
                                id: shipmentId,
                                shipmentReference: (order as any).shipment_reference,
                                masterWayBill: (order as any).master_way_bill,
                              });
                            } else if (onViewAllClick) {
                              onViewAllClick(order);
                            }
                          }}
                          className={isAdjustmentAction
                            ? 'inline-flex items-center justify-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-400 text-sm font-medium whitespace-nowrap shrink-0'
                            : isFulfillmentPage
                              ? 'inline-flex items-center justify-center gap-3 rounded-full bg-[#E8EAFA] px-5 py-2 text-[15px] font-semibold text-[#8C94FF] whitespace-nowrap shrink-0 transition-colors hover:bg-[#DDE3F4]'
                              : 'inline-flex items-center justify-center gap-1 rounded-lg border border-[#C9D4FF] bg-[#EEF4FF] px-3 py-1 text-sm font-medium text-[#8C94FF] whitespace-nowrap shrink-0 transition-colors hover:bg-[#E6EEFF]'}
                        >
                          {isDestInbound ? (
                            <img src="/img/delivery-3.svg" alt="Receive" className="h-4 w-4" />
                          ) : !isAdjustmentAction ? (
                              <img src="/img/eye.svg" alt="View" className="h-5 w-5" />
                          ) : null}
                          {isDestInbound ? 'Receive' : isAdjustmentAction ? 'Adjustment' : t('common.viewAll')}
                        </button>
                      )}

                      {showRefundButton && (
                        <button
                          onClick={() => onRefundClick(order)}
                          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#E8EAFA] px-4 py-1.5 text-[15px] font-medium text-[#8C94FF] transition-colors hover:bg-[#DDE3F4]"
                        >
                          <img src="/img/refund.svg" alt="Refund" className="h-[17px] w-[17px]" />
                          Refund
                        </button>
                      )}
                    </div>
                  </td>
                  )}
                  {!hiddenColumns.orderId && (
                  <td className={`py-3 px-4 text-sm text-gray-900 border-r border-gray-200 ${useActionOrderSpacingDesign ? 'pl-8' : ''}`}>
                    {isShipmentData 
                      ? (order as any).shipment_reference || (order as any).code
                      : isGraphQLOrder 
                        ? (order.order_products && order.order_products.length > 0 
                            ? order.order_products[0].code 
                            : order.reference)
                        : order.code
                    }
                  </td>
                  )}
                  {!hiddenColumns.zone && (
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                    {/* Order Date for destination warehouse local tabs, Zone for others */}
                    {isShipmentData && isDestWarehouseLocal
                      ? new Date((order as any).created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                      : isShipmentData 
                        ? (order as any).mkt_name || 'N/A'
                        : isGraphQLOrder 
                          ? order.market_place_type?.name 
                          : order.first_identifier
                    }
                  </td>
                  )}
                  {!hiddenColumns.mkt && (
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                    {/* Marketplace for destination warehouse local tabs, MKT for others */}
                    {isShipmentData && isDestWarehouseLocal
                      ? (order as any).mkt_name || 'N/A'
                      : isShipmentData 
                        ? (order as any).mkt_name || 'N/A'
                        : isGraphQLOrder 
                          ? (order.order_products && order.order_products.length > 0 
                              ? order.order_products[0].marketplace?.name 
                              : order.market_place_type?.name)
                          : `${order.first_identifier}-${order.second_identifier}`
                    }
                  </td>
                  )}
                  {!hiddenColumns.orderDate && (
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                    {/* Quantity for destination warehouse local tabs, Order Date for others */}
                    {isShipmentData && isDestWarehouseLocal
                      ? (order as any).qty || 1
                      : isShipmentData 
                        ? new Date((order as any).created_at).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                        : isGraphQLOrder 
                          ? new Date(order.order_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                          : (order.order?.order_date
                              ? new Date(order.order.order_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
                              : 'N/A')
                    }
                  </td>
                  )}
                  {!hiddenColumns.identifier && (
                  <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                    {/* Master Way Bill for destination warehouse local tabs, Identifier for others */}
                    {isShipmentData && isDestWarehouseLocal
                      ? (order as any).master_way_bill || 'N/A'
                      : isShipmentData 
                        ? `${(order as any).first_identifier || (order as any).code}-${(order as any).second_identifier || ''}`
                        : isGraphQLOrder 
                          ? getGraphQLIdentifier(order) 
                          : `${order.first_identifier}-${order.second_identifier}`
                    }
                  </td>
                    )}
                    {!hiddenColumns.shippingTo && (
                    <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                    {/* Weight for destination warehouse local tabs, Shipping To for others */}
                    {isShipmentData && isDestWarehouseLocal
                      ? (order as any).weight ? `${(order as any).weight} kg` : 'N/A'
                      : isShipmentData 
                        ? (order as any).mkt_name || 'N/A'
                        : isGraphQLOrder 
                          ? order.city?.name 
                          : order.order?.email || 'N/A'
                    }
                  </td>
                  )}
                  {!hiddenColumns.status && (
                  <td className="py-3 px-4">
                    {/* Total for destination warehouse local tabs, Status for others */}
                    {isShipmentData && isDestWarehouseLocal
                      ? (order as any).total || 'N/A'
                      : (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColorByStatus(
                          isShipmentData 
                            ? 'shipped' 
                            : isGraphQLOrder 
                              ? (order.order_products && order.order_products.length > 0 
                                  ? order.order_products[0].marketplace_order_product?.status || order.status
                                  : order.status)
                              : order.marketplace_order_product.status
                        )}`}>
                          {(() => {
                            const rawStatus = isShipmentData 
                              ? 'shipped' 
                              : isGraphQLOrder 
                                ? (order.order_products && order.order_products.length > 0 
                                    ? order.order_products[0].marketplace_order_product?.status || order.status
                                    : order.status)
                                : order.marketplace_order_product.status;
                            // Capitalise first letter of each word for display
                            return rawStatus
                              ? rawStatus.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                              : rawStatus;
                          })()}
                        </span>
                      )
                    }
                  </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer with pagination */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
          <span className="text-sm text-gray-700">per page</span>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">
            {useGraphQL 
              ? `${((page - 1) * itemsPerPage) + 1}-${Math.min(page * itemsPerPage, total)} of ${total}`
              : `${((page - 1) * itemsPerPage) + 1}-${Math.min(page * itemsPerPage, total)} of ${total}`
            }
          </span>
          
          <div className="flex items-center gap-1">
            <button 
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            
            {showLeadingEllipsis && (
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  className="px-3 py-1 text-sm rounded text-gray-500 hover:bg-gray-100"
                >
                  1
                </button>
                <span className="px-1 text-sm text-gray-400">...</span>
              </>
            )}

            {visiblePageNumbers.map((pageNum) => {
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                                     className={`px-3 py-1 text-sm rounded ${
                     page === pageNum 
                       ? 'bg-[#F1F1F4] text-gray-900 font-medium' 
                       : 'text-gray-500 hover:bg-gray-100'
                   }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {showTrailingEllipsis && (
              <>
                <span className="px-1 text-sm text-gray-400">...</span>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className="px-3 py-1 text-sm rounded text-gray-500 hover:bg-gray-100"
                >
                  {totalPages}
                </button>
              </>
            )}
            
            <button 
              onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}